import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen lightbox with next/prev arrows and keyboard nav.
 * Props:
 *  - images: array of src urls
 *  - index: current index
 *  - onClose(), onNav(newIndex)
 */
export const Lightbox = ({ images, index, onClose, onNav }) => {
  const total = images?.length || 0;

  const goPrev = useCallback(() => {
    if (!total) return;
    onNav((index - 1 + total) % total);
  }, [index, total, onNav]);

  const goNext = useCallback(() => {
    if (!total) return;
    onNav((index + 1) % total);
  }, [index, total, onNav]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, goPrev, goNext]);

  if (!total) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
        onClick={onClose}
        data-testid="lightbox"
      >
        {/* Close */}
        <button
          data-testid="lightbox-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-5 right-5 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors duration-200"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        {/* Counter */}
        <span className="absolute top-6 left-6 z-10 text-white/80 text-xs tracking-[0.3em] uppercase font-semibold">
          {index + 1} <span className="text-white/40">/</span> {total}
        </span>

        {/* Prev */}
        {total > 1 && (
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 md:left-8 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors duration-200"
            aria-label="Previous"
          >
            <ChevronLeft size={26} />
          </button>
        )}

        {/* Image */}
        <motion.img
          key={images[index]}
          src={images[index]}
          alt={`Image ${index + 1} of ${total}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="max-w-[92vw] max-h-[86vh] object-contain rounded-xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Next */}
        {total > 1 && (
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 md:right-8 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors duration-200"
            aria-label="Next"
          >
            <ChevronRight size={26} />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
